import type { TextAlertEvent } from '@shared/types'

import { OVERLAY_CONFIG } from '@shared/config'
import { type EffectComponentProps } from '@shared/lib/effect-registry'
import { chainAnimations, prefersReducedMotion } from '@shared/lib/waapi'
import { OverlayEffectSlot } from '@shared/ui/OverlayEffectSlot'
import { useEffect, useRef } from 'react'

import styles from './TextAlert.module.css'

// Карточка алерта сама задаёт свою ширину (min/max-width в .alert) — дефолтные
// width:35% у зоны top-center (рассчитан на видео/картинки custom_effects) здесь не нужен.
const SLOT_STYLE_OVERRIDE = { width: 'auto', height: 'auto' } as const

export function TextAlert({ event, onDone }: EffectComponentProps<TextAlertEvent>) {
  const rootRef   = useRef<HTMLDivElement>(null)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  const duration    = event.payload.duration ?? OVERLAY_CONFIG.TEXT_ALERT_DEFAULT_DURATION_MS
  const accentColor = event.payload.color    ?? '#ff9f1c'

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    if (prefersReducedMotion()) {
      el.style.opacity = '1'
      el.style.transform = 'translateY(0)'
      const t = setTimeout(() => onDoneRef.current(), duration)
      return () => clearTimeout(t)
    }
    // translateX(-50%) больше не нужен в анимации: центрирование по X теперь
    // делает родительский OverlayEffectSlot (zone="top-center"), анимируется только вертикаль.
    const hold = Math.max(0, duration - 1000)
    void chainAnimations([
      () => el.animate([{transform:'translateY(-140%)',opacity:0},{transform:'translateY(0)',opacity:1}],{duration:500,easing:'cubic-bezier(0.16,1,0.3,1)',fill:'forwards'}),
      () => el.animate([],{duration:hold,fill:'forwards'}),
      () => el.animate([{transform:'translateY(0)',opacity:1},{transform:'translateY(-140%)',opacity:0}],{duration:500,easing:'cubic-bezier(0.7,0,0.84,0)',fill:'forwards'}),
    ]).then(() => onDoneRef.current())
    return () => el.getAnimations().forEach((a) => a.cancel())
  }, [duration])

  return (
    <OverlayEffectSlot zone="top-center" animateIn={false} style={SLOT_STYLE_OVERRIDE}>
      <div ref={rootRef} className={styles.alert} role="status" aria-live="polite">
        {event.payload.from && (
          <span className={styles.from} style={{ color: accentColor }}>
            {event.payload.from}
          </span>
        )}
        <p className={styles.message}>{event.payload.message}</p>
      </div>
    </OverlayEffectSlot>
  )
}
